import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Icon, Input, Loader, Message, Modal, Segment, Table } from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import filasVtmeService, {
  FilasVtmeStageQueueDto,
  FilasVtmeStageQueueInfoDto,
  FilasVtmeStageSlaItemDto,
  FilasVtmeStageSlaResponseDto,
} from '../../services/filasVtme/filasVtmeService';
import { extractApiErrorMessage } from '../../services/utils/apiErrorUtils';
import {
  FILAS_VTME_THEME,
  filasVtmeModalActionsStyle,
  filasVtmeModalContentStyle,
  filasVtmeModalHeaderStyle,
  filasVtmePageStyle,
  filasVtmeQueueCardStyle,
} from './filasVtmePageTheme';
import FilasVtmeQueuePedidosTable, {
  applyQueuePedidosTableView,
  describeQueuePedidosTableFilters,
  EMPTY_QUEUE_PEDIDOS_VIEW,
  type QueuePedidosTableViewState,
} from './FilasVtmeQueuePedidosTable';
import FilasVtmeRecorteBar from './FilasVtmeRecorteBar';
import FilasVtmeReportScheduleModal from './FilasVtmeReportScheduleModal';
import FilasVtmeHelpPopup from './FilasVtmeHelpPopup';
import {
  DURATION_UNIT_OPTIONS,
  durationValueFromMinutes,
  durationValueToMinutes,
  formatDurationFromMinutes,
  pickDurationUnit,
  type DurationUnit,
} from './filasVtmeDuration';
import { formatFilasVtmeDateOnly, formatFilasVtmeDateTime, formatFilasVtmeDateTimeOrNull } from './filasVtmeDateTime';
import {
  ANCHOR_OPTIONS,
  FilasVtmeRecorteState,
  isDateInRange,
  loadRecorteState,
  MODE_OPTIONS,
  normalizeRecorteState,
  PERIOD_OPTIONS,
  recorteStatesEqual,
  recorteSummary,
  resolveRecorteRange,
  saveRecorteState,
  SCOPE_OPTIONS,
  type RecorteDateAnchor,
  type RecorteMode,
  type RecortePeriodPreset,
  type RecorteScope,
} from './filasVtmeRecorte';
import type {
  FilasVtmeExportPedidoDto,
  FilasVtmeExportQueueDto,
  FilasVtmeExportRequestDto,
} from '../../services/filasVtme/filasVtmeService';

const REFRESH_MS = 2 * 60 * 1000;
/** Padrão ao criar fila nova: só Tempo ativo; Entradas/Movimentações desligados. */
const DEFAULT_NEW_QUEUE_SLA_MINUTES = 30;
const DEFAULT_NEW_QUEUE_SLA_ENTRIES = 0;
const DEFAULT_NEW_QUEUE_SLA_MOVES = 0;
/** Padrão ao criar status novo: SLAs desligados (só conta SLA da fila). */
const DEFAULT_NEW_STATUS_SLA_MINUTES = 0;
const DEFAULT_NEW_STATUS_SLA_ENTRIES = 0;

const filterItemsByRecorte = (
  items: FilasVtmeStageSlaItemDto[],
  recorte: FilasVtmeRecorteState
): FilasVtmeStageSlaItemDto[] => {
  const range = resolveRecorteRange(recorte);

  return items.filter(item => {
    const open = item.passageOpen !== false && !item.queueExitedAtUtc;

    if (recorte.mode === 'live') {
      if (!open) return false;
      // Em fila agora, período (se houver) recorta por inserção; senão por entrada na fila.
      if (!range.fromUtc && !range.toUtc) return true;
      const anchorIso = item.insercaoUtc || item.queueEnteredAtUtc;
      return isDateInRange(anchorIso, range.fromUtc, range.toUtc);
    }

    if (recorte.mode === 'completed') {
      if (open) return false;
    }
    // completed_and_open: mantém abertos e fechados

    if (!range.fromUtc && !range.toUtc) return true;

    const anchorIso =
      recorte.dateAnchor === 'insercao'
        ? item.insercaoUtc || item.queueEnteredAtUtc
        : recorte.dateAnchor === 'queue_entry'
          ? item.queueEnteredAtUtc
          : item.queueExitedAtUtc || item.queueEnteredAtUtc;

    return isDateInRange(anchorIso, range.fromUtc, range.toUtc);
  });
};

const formatNumber = (value: number, digits = 1) => {
  if (!Number.isFinite(value)) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(digits);
};

/** Mediana — métrica padrão dos cards (robusta a outliers). */
const median = (values: number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
};

const minOf = (values: number[]): number | null =>
  values.length === 0 ? null : Math.min(...values);

const maxOf = (values: number[]): number | null =>
  values.length === 0 ? null : Math.max(...values);

type StatTriple = { min: number | null; med: number | null; max: number | null };

const statsOf = (values: number[]): StatTriple => ({
  min: minOf(values),
  med: median(values),
  max: maxOf(values),
});

const newRowId = () => `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const newQueueLocalId = () => `queue-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

type DraftStatusRow = {
  id: string;
  statusVtme: string;
  slaMinutes: number;
  slaEntries: number;
};

type DraftQueue = {
  localId: string;
  id?: string;
  name: string;
  slaMinutes: number;
  slaEntries: number;
  slaMoves: number;
  statuses: DraftStatusRow[];
};

type StatusToneCounts = {
  ok: number;
  warn: number;
  bad: number;
};

type StatusBreakdown = {
  statusVtme: string;
  count: number;
  slaMinutes: number;
  slaEntries: number;
  tempo: StatTriple;
  entries: StatTriple;
  tones: StatusToneCounts;
};

type QueueCardModel = {
  queue: FilasVtmeStageQueueInfoDto;
  count: number;
  tempo: StatTriple;
  entries: StatTriple;
  moves: StatTriple;
  tones: StatusToneCounts;
  items: FilasVtmeStageSlaItemDto[];
  statuses: StatusBreakdown[];
};

const SLA_COLORS = {
  ok: { border: '#2e7d32', value: '#1b5e20', soft: '#e8f5e9' },
  warn: { border: '#f9a825', value: '#f57f17', soft: '#fff8e1' },
  bad: { border: '#c62828', value: '#b71c1c', soft: '#ffebee' },
  neutral: { border: FILAS_VTME_THEME.border, value: FILAS_VTME_THEME.text, soft: '#fff' },
} as const;

type SlaTone = keyof typeof SLA_COLORS;

/** SLA > 0 está ativo; 0/vazio/null = desligado (não contabiliza). */
const isSlaEnabled = (sla: number | null | undefined): boolean =>
  typeof sla === 'number' && Number.isFinite(sla) && sla > 0;

/** Verde: >20% abaixo do SLA; amarelo: até o SLA; vermelho: acima. */
const slaTone = (value: number | null, sla: number | null | undefined): SlaTone => {
  if (value == null || !isSlaEnabled(sla)) return 'neutral';
  const warnFloor = sla! * 0.8;
  if (value > sla!) return 'bad';
  if (value >= warnFloor) return 'warn';
  return 'ok';
};

const worseTone = (a: SlaTone, b: SlaTone): SlaTone => {
  const rank: Record<SlaTone, number> = { bad: 3, warn: 2, ok: 1, neutral: 0 };
  return rank[a] >= rank[b] ? a : b;
};

const itemStatusTone = (
  item: FilasVtmeStageSlaItemDto,
  slaMinutes: number,
  slaEntries: number
): 'ok' | 'warn' | 'bad' => {
  const combined = worseTone(
    slaTone(item.elapsedMinutes, slaMinutes),
    slaTone(item.statusEntries ?? 0, slaEntries)
  );
  return combined === 'neutral' ? 'ok' : combined;
};

const itemQueueTone = (
  item: FilasVtmeStageSlaItemDto,
  slaMinutes: number | null | undefined,
  slaEntries: number | null | undefined,
  slaMoves: number | null | undefined
): 'ok' | 'warn' | 'bad' => {
  const combined = worseTone(
    worseTone(slaTone(item.queueElapsedMinutes ?? 0, slaMinutes), slaTone(item.queueEntries ?? 0, slaEntries)),
    slaTone(item.queueMoves ?? 0, slaMoves)
  );
  return combined === 'neutral' ? 'ok' : combined;
};

const countTones = (
  items: FilasVtmeStageSlaItemDto[],
  classify: (item: FilasVtmeStageSlaItemDto) => 'ok' | 'warn' | 'bad'
): StatusToneCounts => {
  const tones: StatusToneCounts = { ok: 0, warn: 0, bad: 0 };
  for (const item of items) {
    tones[classify(item)] += 1;
  }
  return tones;
};

const buildStatusBreakdown = (
  statusVtme: string,
  group: FilasVtmeStageSlaItemDto[],
  slaMinutes: number,
  slaEntries: number
): StatusBreakdown => {
  const tones: StatusToneCounts = { ok: 0, warn: 0, bad: 0 };
  for (const item of group) {
    tones[itemStatusTone(item, slaMinutes, slaEntries)] += 1;
  }
  return {
    statusVtme,
    count: group.length,
    slaMinutes,
    slaEntries,
    tempo: statsOf(group.map(item => item.elapsedMinutes)),
    entries: statsOf(group.map(item => item.statusEntries ?? 0)),
    tones,
  };
};

const buildQueueCardModel = (
  queue: FilasVtmeStageQueueInfoDto,
  items: FilasVtmeStageSlaItemDto[]
): QueueCardModel => {
  const queueItems = items.filter(item => item.queueId === queue.id);
  const tempoValues = queueItems.map(item => item.queueElapsedMinutes ?? 0);
  const entryValues = queueItems.map(item => item.queueEntries ?? 0);
  const moveValues = queueItems.map(item => item.queueMoves ?? 0);

  const configured = queue.statuses ?? [];
  const byStatus = new Map<string, FilasVtmeStageSlaItemDto[]>();
  for (const item of queueItems) {
    const key = item.statusVtme || '—';
    const list = byStatus.get(key) ?? [];
    list.push(item);
    byStatus.set(key, list);
  }

  const statuses: StatusBreakdown[] = [];
  const seen = new Set<string>();

  for (const cfg of configured) {
    const label = cfg.statusVtme;
    seen.add(label);
    statuses.push(
      buildStatusBreakdown(label, byStatus.get(label) ?? [], cfg.slaMinutes, cfg.slaEntries)
    );
  }

  Array.from(byStatus.entries()).forEach(([label, group]) => {
    if (seen.has(label)) return;
    statuses.push(
      buildStatusBreakdown(
        label,
        group,
        group[0]?.slaMinutes ?? 0,
        group[0]?.slaEntries ?? 0
      )
    );
  });

  return {
    queue,
    count: queueItems.length,
    tempo: statsOf(tempoValues),
    entries: statsOf(entryValues),
    moves: statsOf(moveValues),
    tones: countTones(queueItems, item =>
      itemQueueTone(item, queue.slaMinutes, queue.slaEntries, queue.slaMoves)
    ),
    items: queueItems,
    statuses,
  };
};

const formatRangeHint = (minText: string, maxText: string) => `Mín: ${minText} · Máx: ${maxText}`;

const SlaMetricCard: React.FC<{
  label: string;
  valueText: string;
  rawValue: number | null;
  sla: number | null | undefined;
  slaSuffix?: string;
  slaIsDuration?: boolean;
  rangeHint?: string;
  compact?: boolean;
}> = ({ label, valueText, rawValue, sla, slaSuffix = '', slaIsDuration = false, rangeHint, compact = false }) => {
  const tone = slaTone(rawValue, sla);
  const colors = SLA_COLORS[tone];
  const slaLabel =
    sla == null
      ? 'SLA —'
      : slaIsDuration
        ? `SLA ≤ ${formatDurationFromMinutes(sla)}`
        : `SLA ≤ ${sla}${slaSuffix}`;

  return (
    <div
      style={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        background: '#fff',
        border: `${compact ? 1 : 2}px solid ${colors.border}`,
        borderRadius: 8,
        padding: compact ? '8px 8px' : '12px 10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: compact ? 4 : 6,
      }}
    >
      {rangeHint && (
        <FilasVtmeHelpPopup text={rangeHint} position="bottom right" compact corner />
      )}
      <div
        style={{
          fontSize: compact ? 16 : 20,
          fontWeight: 800,
          lineHeight: 1.1,
          color: colors.value,
        }}
      >
        {valueText}
      </div>
      <div style={{ fontSize: compact ? 10 : 11, fontWeight: 600, color: FILAS_VTME_THEME.muted, lineHeight: 1.3 }}>
        {label}
        <div style={{ fontWeight: 500, marginTop: 2 }}>{slaLabel}</div>
      </div>
    </div>
  );
};

const StatusCountCell: React.FC<{
  value: string | number;
  label: string;
  color?: string;
}> = ({ value, label, color }) => (
  <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
    <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1, color: color ?? FILAS_VTME_THEME.text }}>
      {value}
    </div>
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.3,
        color: FILAS_VTME_THEME.muted,
        marginTop: 4,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
  </div>
);

const StatusDetailCard: React.FC<{
  status: StatusBreakdown;
  empty: boolean;
}> = ({ status, empty }) => {
  const tempoMin = status.tempo.min == null ? '—' : formatDurationFromMinutes(status.tempo.min);
  const tempoMax = status.tempo.max == null ? '—' : formatDurationFromMinutes(status.tempo.max);
  const entriesMin = status.entries.min == null ? '—' : formatNumber(status.entries.min);
  const entriesMax = status.entries.max == null ? '—' : formatNumber(status.entries.max);
  const showTempo = isSlaEnabled(status.slaMinutes);
  const showEntradas = isSlaEnabled(status.slaEntries);
  const showToneCounts = showTempo || showEntradas;
  /** Sem SLA de status: card compacto (nome + total) para caber vários por linha. */
  const compactOnlyTotal = !showTempo && !showEntradas;

  if (compactOnlyTotal) {
    return (
      <div
        style={{
          background: '#fff',
          border: `1px solid ${FILAS_VTME_THEME.border}`,
          borderRadius: 8,
          padding: '10px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minWidth: 0,
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            fontWeight: 700,
            color: FILAS_VTME_THEME.text,
            fontSize: 13,
            lineHeight: 1.3,
            wordBreak: 'break-word',
          }}
          title={status.statusVtme}
        >
          {status.statusVtme}
        </div>
        <div style={{ flex: '0 0 auto', textAlign: 'right' }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              lineHeight: 1.05,
              color: FILAS_VTME_THEME.accentLilac,
            }}
          >
            {empty ? '—' : status.count}
          </div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.3,
              color: FILAS_VTME_THEME.muted,
              marginTop: 2,
              textTransform: 'uppercase',
            }}
          >
            Total
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${FILAS_VTME_THEME.border}`,
        borderRadius: 8,
        padding: '12px',
        minWidth: 0,
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          fontWeight: 700,
          color: FILAS_VTME_THEME.text,
          fontSize: 14,
          marginBottom: 10,
          lineHeight: 1.3,
          wordBreak: 'break-word',
        }}
        title={status.statusVtme}
      >
        {status.statusVtme}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: showTempo || showEntradas ? 14 : 0 }}>
        <StatusCountCell value={empty ? '—' : status.count} label="Total" color={FILAS_VTME_THEME.accentLilac} />
        {showToneCounts && (
          <>
            <StatusCountCell value={empty ? '—' : status.tones.ok} label="Ok" color={SLA_COLORS.ok.value} />
            <StatusCountCell value={empty ? '—' : status.tones.warn} label="Atenção" color={SLA_COLORS.warn.value} />
            <StatusCountCell value={empty ? '—' : status.tones.bad} label="Fora" color={SLA_COLORS.bad.value} />
          </>
        )}
      </div>

      {(showTempo || showEntradas) && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {showTempo && (
            <SlaMetricCard
              label="Mediana Tempo"
              valueText={empty || status.tempo.med == null ? '—' : formatDurationFromMinutes(status.tempo.med)}
              rawValue={empty ? null : status.tempo.med}
              sla={status.slaMinutes}
              slaIsDuration
              rangeHint={empty ? undefined : formatRangeHint(tempoMin, tempoMax)}
              compact
            />
          )}
          {showEntradas && (
            <SlaMetricCard
              label="Mediana Entradas"
              valueText={empty || status.entries.med == null ? '—' : formatNumber(status.entries.med)}
              rawValue={empty ? null : status.entries.med}
              sla={status.slaEntries}
              rangeHint={empty ? undefined : formatRangeHint(entriesMin, entriesMax)}
              compact
            />
          )}
        </div>
      )}
    </div>
  );
};

const QueueDetailCard: React.FC<{
  model: QueueCardModel;
  tableItems: FilasVtmeStageSlaItemDto[];
  pending: boolean;
  unavailable: boolean;
  exporting?: boolean;
  onExport?: (format: 'xlsx' | 'pdf') => void;
  onTableViewChange?: (state: QueuePedidosTableViewState) => void;
}> = ({ model, tableItems, pending, unavailable, exporting = false, onExport, onTableViewChange }) => {
  const [expanded, setExpanded] = useState(true);
  const empty = pending || unavailable;
  const { queue, count, tempo, entries, moves, tones, statuses } = model;
  const showTempo = isSlaEnabled(queue.slaMinutes);
  const showEntradas = isSlaEnabled(queue.slaEntries);
  const showMoves = isSlaEnabled(queue.slaMoves);

  const tempoHint =
    empty || tempo.min == null || tempo.max == null
      ? undefined
      : formatRangeHint(formatDurationFromMinutes(tempo.min), formatDurationFromMinutes(tempo.max));
  const entriesHint =
    empty || entries.min == null || entries.max == null
      ? undefined
      : formatRangeHint(formatNumber(entries.min), formatNumber(entries.max));
  const movesHint =
    empty || moves.min == null || moves.max == null
      ? undefined
      : formatRangeHint(formatNumber(moves.min), formatNumber(moves.max));

  const toggleExpanded = () => setExpanded(prev => !prev);

  return (
    <Segment
      style={{ ...filasVtmeQueueCardStyle, padding: 16 }}
      onDoubleClick={toggleExpanded}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 10,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            toggleExpanded();
          }}
          onDoubleClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
          aria-label={expanded ? 'Recolher detalhes da fila' : 'Expandir detalhes da fila'}
          aria-expanded={expanded}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            margin: 0,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            color: FILAS_VTME_THEME.muted,
            lineHeight: 1,
          }}
        >
          <Icon
            name={expanded ? 'chevron down' : 'chevron right'}
            style={{ margin: 0 }}
          />
        </button>
        <div style={{ fontWeight: 700, fontSize: 18, color: FILAS_VTME_THEME.text, flex: 1 }}>{queue.name}</div>
        {onExport && (
          <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}>
            <Button
              type="button"
              size="mini"
              disabled={exporting || empty}
              loading={exporting}
              onClick={() => onExport('xlsx')}
            >
              <Icon name="file excel" style={{ border: 'none', boxShadow: 'none', background: 'transparent' }} />
              XLSX
            </Button>
            <Button
              type="button"
              size="mini"
              disabled={exporting || empty}
              loading={exporting}
              onClick={() => onExport('pdf')}
            >
              <Icon name="file pdf" style={{ border: 'none', boxShadow: 'none', background: 'transparent' }} />
              PDF
            </Button>
          </div>
        )}
      </div>

      {empty ? (
        <div style={{ fontSize: 13, color: FILAS_VTME_THEME.muted, marginBottom: 12 }}>
          {pending ? 'Carregando…' : 'Fila indisponível'}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <StatusCountCell value={count} label="Total" color={FILAS_VTME_THEME.accentLilac} />
          <StatusCountCell value={tones.ok} label="Ok" color={SLA_COLORS.ok.value} />
          <StatusCountCell value={tones.warn} label="Atenção" color={SLA_COLORS.warn.value} />
          <StatusCountCell value={tones.bad} label="Fora" color={SLA_COLORS.bad.value} />
        </div>
      )}

      {(showTempo || showEntradas || showMoves) && (
        <div style={{ display: 'flex', gap: 10 }}>
          {showTempo && (
            <SlaMetricCard
              label="Mediana Tempo na Fila"
              valueText={empty || tempo.med == null ? '—' : formatDurationFromMinutes(tempo.med)}
              rawValue={empty ? null : tempo.med}
              sla={queue.slaMinutes}
              slaIsDuration
              rangeHint={tempoHint}
            />
          )}
          {showEntradas && (
            <SlaMetricCard
              label="Mediana Entradas na fila"
              valueText={empty || entries.med == null ? '—' : formatNumber(entries.med)}
              rawValue={empty ? null : entries.med}
              sla={queue.slaEntries}
              rangeHint={entriesHint}
            />
          )}
          {showMoves && (
            <SlaMetricCard
              label="Mediana Movimentações na fila"
              valueText={empty || moves.med == null ? '—' : formatNumber(moves.med)}
              rawValue={empty ? null : moves.med}
              sla={queue.slaMoves}
              rangeHint={movesHint}
            />
          )}
        </div>
      )}

      {expanded && (
        <>
          <div style={{ marginTop: 14, fontSize: 12, fontWeight: 700, color: FILAS_VTME_THEME.muted }}>
            Status da fila
          </div>
          {statuses.length === 0 ? (
            <div style={{ fontSize: 12, color: FILAS_VTME_THEME.muted, marginTop: 6 }}>
              Nenhum status configurado.
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: statuses.some(
                  s => isSlaEnabled(s.slaMinutes) || isSlaEnabled(s.slaEntries)
                )
                  ? 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))'
                  : 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
                gap: 10,
                marginTop: 8,
                alignItems: 'stretch',
              }}
            >
              {statuses.map(status => (
                <StatusDetailCard key={status.statusVtme} status={status} empty={empty} />
              ))}
            </div>
          )}

          <FilasVtmeQueuePedidosTable
            items={tableItems}
            slaMinutes={queue.slaMinutes}
            slaEntries={queue.slaEntries}
            slaMoves={queue.slaMoves}
            unavailable={unavailable}
            onViewStateChange={onTableViewChange}
          />
        </>
      )}
    </Segment>
  );
};

const FilasVtmePage: React.FC = () => {
  const { user } = useAuth();
  const { showError } = useToast();
  const userId = user?.id || null;

  const [data, setData] = useState<FilasVtmeStageSlaResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const configAbortRef = useRef<AbortController | null>(null);
  const [recorte, setRecorte] = useState<FilasVtmeRecorteState>(() => loadRecorteState(userId));
  const [savedRecorte, setSavedRecorte] = useState<FilasVtmeRecorteState>(() => loadRecorteState(userId));
  const [recorteSaving, setRecorteSaving] = useState(false);
  const preferenceLoadedRef = useRef(false);
  const [tableViews, setTableViews] = useState<Record<string, QueuePedidosTableViewState>>({});
  const [exportingKey, setExportingKey] = useState<string | null>(null);

  const handleTableViewChange = useCallback((queueId: string, state: QueuePedidosTableViewState) => {
    setTableViews(prev => {
      const current = prev[queueId];
      if (
        current &&
        current.sort?.column === state.sort?.column &&
        current.sort?.dir === state.sort?.dir &&
        current.filters.pedido === state.filters.pedido &&
        current.filters.cliente === state.filters.cliente &&
        current.filters.cnpj === state.filters.cnpj &&
        current.filters.statuses.join('|') === state.filters.statuses.join('|')
      ) {
        return prev;
      }
      return { ...prev, [queueId]: state };
    });
  }, []);

  const [configOpen, setConfigOpen] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [queuesDraft, setQueuesDraft] = useState<DraftQueue[]>([]);

  // Carrega preferência: banco → localStorage → padrão (falhas silenciosas).
  useEffect(() => {
    if (!userId || preferenceLoadedRef.current) return;
    preferenceLoadedRef.current = true;
    let cancelled = false;

    const bootstrap = async () => {
      const local = loadRecorteState(userId);
      try {
        const remote = await filasVtmeService.getRecortePreference();
        if (cancelled) return;
        if (remote?.found) {
          const fromDb = normalizeRecorteState({
            mode: remote.mode as RecorteMode,
            dateAnchor: remote.dateAnchor as RecorteDateAnchor,
            periodPreset: remote.periodPreset as RecortePeriodPreset,
            fromDate: remote.fromDate || '',
            toDate: remote.toDate || '',
            scope: remote.scope as RecorteScope,
          });
          setRecorte(fromDb);
          setSavedRecorte(fromDb);
          saveRecorteState(fromDb, userId);
          return;
        }
      } catch {
        // silencioso — cai no localStorage
      }
      if (cancelled) return;
      setRecorte(local);
      setSavedRecorte(local);
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const range = resolveRecorteRange(recorte);
      const response = await filasVtmeService.listStageSla({
        signal: controller.signal,
        mode: recorte.mode,
        dateAnchor: recorte.dateAnchor,
        diasInseridos: range.diasInseridos,
        fromUtc: range.fromUtc?.toISOString() ?? null,
        toUtc: range.toUtc?.toISOString() ?? null,
      });
      if (!controller.signal.aborted) setData(response);
    } catch (err: unknown) {
      if (controller.signal.aborted) return;
      setError(extractApiErrorMessage(err, 'Não foi possível carregar as filas.'));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [recorte]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      window.clearInterval(timer);
      abortRef.current?.abort();
      configAbortRef.current?.abort();
    };
  }, [load]);

  const recorteDirty = !recorteStatesEqual(recorte, savedRecorte);

  const handleRecorteChange = (next: FilasVtmeRecorteState) => {
    setRecorte(next);
  };

  const handleSaveRecorte = useCallback(async () => {
    setRecorteSaving(true);
    try {
      await filasVtmeService.saveRecortePreference({
        mode: recorte.mode,
        dateAnchor: recorte.dateAnchor,
        periodPreset: recorte.periodPreset,
        fromDate: recorte.fromDate,
        toDate: recorte.toDate,
        scope: recorte.scope,
      });
      saveRecorteState(recorte, userId);
      setSavedRecorte(recorte);
    } catch (err: unknown) {
      showError(extractApiErrorMessage(err, 'Não foi possível salvar os filtros. Tente novamente.'));
    } finally {
      setRecorteSaving(false);
    }
  }, [recorte, userId, showError]);

  const closeConfig = useCallback(() => {
    if (configSaving) return;
    configAbortRef.current?.abort();
    setConfigOpen(false);
  }, [configSaving]);

  const openConfig = useCallback(async () => {
    configAbortRef.current?.abort();
    const controller = new AbortController();
    configAbortRef.current = controller;

    setConfigOpen(true);
    setConfigLoading(true);
    setConfigError(null);
    try {
      // Cache local (Gettel DB) — rápido; não bloqueia no webhook.
      const [settings, statuses] = await Promise.all([
        filasVtmeService.getStageQueueSettings({ signal: controller.signal }),
        filasVtmeService.listVtmeStatuses({ signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return;

      setAvailableStatuses(statuses);
      setQueuesDraft(
        [...settings.queues]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(queue => ({
            localId: queue.id || newQueueLocalId(),
            id: queue.id,
            name: queue.name,
            slaMinutes: Math.max(0, queue.slaMinutes ?? 0),
            slaEntries: Math.max(0, queue.slaEntries ?? 0),
            slaMoves: Math.max(0, queue.slaMoves ?? 0),
            statuses: (queue.statuses || []).map(status => ({
              id: newRowId(),
              statusVtme: status.statusVtme,
              slaMinutes: Math.max(0, status.slaMinutes ?? 0),
              slaEntries: Math.max(0, status.slaEntries ?? 0),
            })),
          }))
      );
      setConfigLoading(false);

      // Refresh silencioso do webhook: atualiza combobox se houver status novos.
      void filasVtmeService
        .syncVtmeStatuses({ signal: controller.signal })
        .then(synced => {
          if (controller.signal.aborted) return;
          setAvailableStatuses(prev => {
            if (synced.length === 0) return prev;
            const merged = Array.from(new Set([...prev, ...synced]));
            return merged.sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
          });
        })
        .catch(() => {
          /* silencioso: modal já funciona com o cache */
        });
    } catch (err: unknown) {
      if (controller.signal.aborted) return;
      setConfigError(extractApiErrorMessage(err, 'Não foi possível carregar a configuração.'));
      setConfigLoading(false);
    }
  }, []);

  const saveConfig = useCallback(async () => {
    setConfigSaving(true);
    setConfigError(null);
    try {
      if (queuesDraft.length === 0) {
        throw new Error('Informe pelo menos uma fila.');
      }
      if (queuesDraft.some(queue => !queue.name.trim())) {
        throw new Error('Todas as filas precisam de um nome.');
      }
      const queueSemSla = queuesDraft.find(
        queue =>
          !isSlaEnabled(queue.slaMinutes)
          && !isSlaEnabled(queue.slaEntries)
          && !isSlaEnabled(queue.slaMoves)
      );
      if (queueSemSla) {
        throw new Error(
          `Informe ao menos um SLA ativo na fila "${queueSemSla.name.trim() || 'sem nome'}" (Tempo, Entradas ou Movimentações). Zero desliga o indicador.`
        );
      }

      const queues: FilasVtmeStageQueueDto[] = queuesDraft.map((queue, index) => ({
        id: queue.id,
        name: queue.name.trim(),
        sortOrder: index,
        slaMinutes: Math.max(0, queue.slaMinutes || 0),
        slaEntries: Math.max(0, queue.slaEntries || 0),
        slaMoves: Math.max(0, queue.slaMoves || 0),
        statuses: queue.statuses
          .filter(row => row.statusVtme.trim().length > 0)
          .map(row => ({
            statusVtme: row.statusVtme.trim(),
            slaMinutes: Math.max(0, row.slaMinutes || 0),
            slaEntries: Math.max(0, row.slaEntries || 0),
          })),
      }));

      await filasVtmeService.saveStageQueueSettings(queues);
      configAbortRef.current?.abort();
      setConfigOpen(false);
      await load();
    } catch (err: unknown) {
      setConfigError(
        err instanceof Error && err.message && !('response' in err)
          ? err.message
          : extractApiErrorMessage(err, 'Não foi possível salvar a configuração.')
      );
    } finally {
      setConfigSaving(false);
    }
  }, [queuesDraft, load]);

  const addQueue = () => {
    setQueuesDraft(rows => [
      ...rows,
      {
        localId: newQueueLocalId(),
        name: `Nova fila ${rows.length + 1}`,
        slaMinutes: DEFAULT_NEW_QUEUE_SLA_MINUTES,
        slaEntries: DEFAULT_NEW_QUEUE_SLA_ENTRIES,
        slaMoves: DEFAULT_NEW_QUEUE_SLA_MOVES,
        statuses: [],
      },
    ]);
  };

  const removeQueue = (localId: string) => {
    setQueuesDraft(rows => rows.filter(queue => queue.localId !== localId));
  };

  const moveQueue = (localId: string, direction: -1 | 1) => {
    setQueuesDraft(rows => {
      const index = rows.findIndex(queue => queue.localId === localId);
      if (index < 0) return rows;
      const target = index + direction;
      if (target < 0 || target >= rows.length) return rows;
      const next = [...rows];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  };

  const updateQueueName = (localId: string, name: string) => {
    setQueuesDraft(rows => rows.map(queue => (queue.localId === localId ? { ...queue, name } : queue)));
  };

  const updateQueueSla = (
    localId: string,
    patch: Partial<Pick<DraftQueue, 'slaMinutes' | 'slaEntries' | 'slaMoves'>>
  ) => {
    setQueuesDraft(rows =>
      rows.map(queue => (queue.localId === localId ? { ...queue, ...patch } : queue))
    );
  };

  const addStatusRow = (localId: string) => {
    setQueuesDraft(rows =>
      rows.map(queue =>
        queue.localId === localId
          ? {
              ...queue,
              statuses: [
                ...queue.statuses,
                {
                  id: newRowId(),
                  statusVtme: '',
                  slaMinutes: DEFAULT_NEW_STATUS_SLA_MINUTES,
                  slaEntries: DEFAULT_NEW_STATUS_SLA_ENTRIES,
                },
              ],
            }
          : queue
      )
    );
  };

  const removeStatusRow = (localId: string, rowId: string) => {
    setQueuesDraft(rows =>
      rows.map(queue =>
        queue.localId === localId
          ? { ...queue, statuses: queue.statuses.filter(row => row.id !== rowId) }
          : queue
      )
    );
  };

  const updateStatusRow = (
    localId: string,
    rowId: string,
    patch: Partial<Pick<DraftStatusRow, 'statusVtme' | 'slaMinutes' | 'slaEntries'>>
  ) => {
    setQueuesDraft(rows =>
      rows.map(queue =>
        queue.localId === localId
          ? {
              ...queue,
              statuses: queue.statuses.map(row => (row.id === rowId ? { ...row, ...patch } : row)),
            }
          : queue
      )
    );
  };

  const items = data?.items ?? [];
  const liveItems = useMemo(
    () => items.filter(item => item.passageOpen !== false && !item.queueExitedAtUtc),
    [items]
  );
  const recorteItems = useMemo(() => filterItemsByRecorte(items, recorte), [items, recorte]);

  const kpiItems = useMemo(() => {
    if (recorte.scope === 'table_only') return liveItems;
    return recorteItems;
  }, [recorte.scope, liveItems, recorteItems]);

  const tableItemsAll = useMemo(() => {
    if (recorte.scope === 'kpis_only') return liveItems;
    return recorteItems;
  }, [recorte.scope, liveItems, recorteItems]);

  const queues = useMemo(
    () => [...(data?.queues ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [data?.queues]
  );
  const cardModels = useMemo(
    () =>
      queues.map(queue => ({
        model: buildQueueCardModel(queue, kpiItems),
        tableItems: tableItemsAll.filter(item => item.queueId === queue.id),
      })),
    [queues, kpiItems, tableItemsAll]
  );
  const countsUnavailable = data?.available === false;
  const countsPending = loading && !data;

  const buildExportRequest = useCallback(
    (format: 'xlsx' | 'pdf', queueId?: string): FilasVtmeExportRequestDto => {
      const models = queueId ? cardModels.filter(c => c.model.queue.id === queueId) : cardModels;
      const periodLabel =
        recorte.periodPreset === 'custom'
          ? `${formatFilasVtmeDateOnly(recorte.fromDate || null)} → ${formatFilasVtmeDateOnly(recorte.toDate || null)}`
          : recorte.periodPreset === 'all'
            ? 'Todos'
            : PERIOD_OPTIONS.find(o => o.value === recorte.periodPreset)?.text || recorte.periodPreset;

      const tableNotes = models
        .map(c => {
          const note = describeQueuePedidosTableFilters(tableViews[c.model.queue.id] || EMPTY_QUEUE_PEDIDOS_VIEW);
          return note ? `${c.model.queue.name}: ${note}` : null;
        })
        .filter((v): v is string => Boolean(v));

      const queuesPayload: FilasVtmeExportQueueDto[] = models.map(({ model }) => {
        const showSlaTempo = isSlaEnabled(model.queue.slaMinutes);
        const showSlaEntradas = isSlaEnabled(model.queue.slaEntries);
        const showSlaMoves = isSlaEnabled(model.queue.slaMoves);
        return {
          queueId: model.queue.id,
          queueName: model.queue.name,
          sortOrder: model.queue.sortOrder,
          total: model.count,
          ok: model.tones.ok,
          warn: model.tones.warn,
          bad: model.tones.bad,
          medianTempo: showSlaTempo
            ? model.tempo.med == null
              ? '—'
              : formatDurationFromMinutes(model.tempo.med)
            : null,
          medianEntradas: showSlaEntradas
            ? model.entries.med == null
              ? '—'
              : formatNumber(model.entries.med)
            : null,
          medianMoves: showSlaMoves
            ? model.moves.med == null
              ? '—'
              : formatNumber(model.moves.med)
            : null,
          slaTempo: showSlaTempo ? formatDurationFromMinutes(model.queue.slaMinutes) : null,
          slaEntradas: showSlaEntradas ? String(model.queue.slaEntries) : null,
          slaMoves: showSlaMoves ? String(model.queue.slaMoves) : null,
          showSlaTempo,
          showSlaEntradas,
          showSlaMoves,
          statuses: model.statuses.map(status => {
            const statusShowTempo = isSlaEnabled(status.slaMinutes);
            const statusShowEntradas = isSlaEnabled(status.slaEntries);
            return {
              statusVtme: status.statusVtme,
              total: status.count,
              ok: status.tones.ok,
              warn: status.tones.warn,
              bad: status.tones.bad,
              medianTempo: statusShowTempo
                ? status.tempo.med == null
                  ? '—'
                  : formatDurationFromMinutes(status.tempo.med)
                : null,
              medianEntradas: statusShowEntradas
                ? status.entries.med == null
                  ? '—'
                  : formatNumber(status.entries.med)
                : null,
              showSlaTempo: statusShowTempo,
              showSlaEntradas: statusShowEntradas,
            };
          }),
        };
      });

      const pedidosPayload: FilasVtmeExportPedidoDto[] = [];
      for (const { model, tableItems } of models) {
        const showSlaTempo = isSlaEnabled(model.queue.slaMinutes);
        const showSlaEntradas = isSlaEnabled(model.queue.slaEntries);
        const showSlaMoves = isSlaEnabled(model.queue.slaMoves);
        const view = tableViews[model.queue.id] || EMPTY_QUEUE_PEDIDOS_VIEW;
        const visible = applyQueuePedidosTableView(
          tableItems,
          view,
          model.queue.slaMinutes,
          model.queue.slaEntries,
          model.queue.slaMoves
        );
        for (const item of visible) {
          const tempoTone = slaTone(item.queueElapsedMinutes ?? 0, model.queue.slaMinutes);
          const movimentosTone = slaTone(item.queueMoves ?? 0, model.queue.slaMoves);
          const entradasTone = slaTone(item.queueEntries ?? 0, model.queue.slaEntries);
          pedidosPayload.push({
            queueId: model.queue.id,
            queueName: model.queue.name,
            pedidoId: item.pedidoId,
            cliente: item.clientName || '',
            cnpj: item.cnpj || '',
            statusVtme: item.statusVtme || '',
            dataInsercao: formatFilasVtmeDateTime(item.insercaoUtc),
            ultimaTrocaStatus: formatFilasVtmeDateTime(item.ultimaTrocaStatusUtc),
            tempoNaFila: showSlaTempo
              ? formatDurationFromMinutes(item.queueElapsedMinutes ?? 0)
              : null,
            movimentos: showSlaMoves ? String(item.queueMoves ?? 0) : null,
            entradas: showSlaEntradas ? String(item.queueEntries ?? 0) : null,
            rowTone: itemQueueTone(
              item,
              model.queue.slaMinutes,
              model.queue.slaEntries,
              model.queue.slaMoves
            ),
            tempoTone,
            movimentosTone,
            entradasTone,
          });
        }
      }

      return {
        format,
        queueId: queueId || null,
        ultimaAtualizacaoVtme: formatFilasVtmeDateTimeOrNull(data?.ultimaAtualizacaoUtc),
        filters: {
          mode: recorte.mode,
          modeLabel: MODE_OPTIONS.find(o => o.value === recorte.mode)?.text || recorte.mode,
          dateAnchor: recorte.dateAnchor,
          dateAnchorLabel: ANCHOR_OPTIONS.find(o => o.value === recorte.dateAnchor)?.text || recorte.dateAnchor,
          period: periodLabel,
          scope: recorte.scope,
          scopeLabel: SCOPE_OPTIONS.find(o => o.value === recorte.scope)?.text || recorte.scope,
          summary: recorteSummary(recorte),
          tableFiltersNote: tableNotes.length ? tableNotes.join(' · ') : null,
        },
        queues: queuesPayload,
        pedidos: pedidosPayload,
      };
    },
    [cardModels, data?.ultimaAtualizacaoUtc, recorte, tableViews]
  );

  const handleExport = useCallback(
    async (format: 'xlsx' | 'pdf', queueId?: string) => {
      const key = `${format}:${queueId || 'all'}`;
      setExportingKey(key);
      try {
        await filasVtmeService.exportStageSla(buildExportRequest(format, queueId));
      } catch (err: unknown) {
        showError(err instanceof Error ? err.message : 'Não foi possível exportar as Filas VTME.');
      } finally {
        setExportingKey(null);
      }
    },
    [buildExportRequest, showError]
  );

  return (
    <GlassPageWrapper>
      <div style={filasVtmePageStyle}>
        <div
          style={{
            background: '#fff',
            border: `1px solid ${FILAS_VTME_THEME.border}`,
            borderRadius: 8,
            padding: 12,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ fontWeight: 700, color: FILAS_VTME_THEME.text }}>
              Filas VTME
              <div style={{ marginTop: 4, fontSize: 12, fontWeight: 400, color: FILAS_VTME_THEME.muted }}>
                Última atualização VTME:{' '}
                {formatFilasVtmeDateTimeOrNull(data?.ultimaAtualizacaoUtc) ?? 'indisponível'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                type="button"
                disabled={!!exportingKey || countsPending || cardModels.length === 0}
                loading={exportingKey === 'xlsx:all'}
                onClick={() => void handleExport('xlsx')}
              >
                <Icon name="file excel" style={{ border: 'none', boxShadow: 'none', background: 'transparent' }} />
                Exportar XLSX
              </Button>
              <Button
                type="button"
                disabled={!!exportingKey || countsPending || cardModels.length === 0}
                loading={exportingKey === 'pdf:all'}
                onClick={() => void handleExport('pdf')}
              >
                <Icon name="file pdf" style={{ border: 'none', boxShadow: 'none', background: 'transparent' }} />
                Exportar PDF
              </Button>
              <FilasVtmeReportScheduleModal
                recorte={recorte}
                queues={queues.map(q => ({ id: q.id, name: q.name }))}
              />
              <Button type="button" onClick={() => void openConfig()}>
                Configurar
              </Button>
              <Button type="button" onClick={() => void load()} disabled={loading}>
                Atualizar
              </Button>
            </div>
          </div>
        </div>
        <FilasVtmeRecorteBar
          value={recorte}
          onChange={handleRecorteChange}
          dirty={recorteDirty}
          saving={recorteSaving}
          onSave={() => void handleSaveRecorte()}
        />
        {recorte.mode !== 'live' && (
          <Message
            info
            style={{ marginTop: 0 }}
            content="Modos de ciclo usam passagens detectadas no histórico dos pedidos que ainda estão (ou voltaram) na fila. Quem saiu e nunca mais entrou pode não aparecer até haver sync de histórico completo. Use “Entenda o recorte” na barra acima para exemplos."
          />
        )}
        {error && <Message negative content={error} />}
        {data && data.available === false && (
          <Message warning content={data.unavailableReason || 'Fila VTME indisponível.'} />
        )}
        {loading && !data ? (
          <Loader active inline="centered" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12, width: '100%' }}>
            {cardModels.length === 0 ? (
              <Segment style={filasVtmeQueueCardStyle}>
                <div style={{ fontWeight: 700, color: FILAS_VTME_THEME.text }}>Sem filas</div>
                <div style={{ fontSize: 12, color: FILAS_VTME_THEME.muted, marginTop: 8 }}>
                  Configure ao menos uma fila para começar.
                </div>
              </Segment>
            ) : (
              cardModels.map(({ model, tableItems }) => (
                <QueueDetailCard
                  key={model.queue.id}
                  model={model}
                  tableItems={tableItems}
                  pending={countsPending}
                  unavailable={countsUnavailable}
                  exporting={!!exportingKey}
                  onExport={format => void handleExport(format, model.queue.id)}
                  onTableViewChange={state => handleTableViewChange(model.queue.id, state)}
                />
              ))
            )}
          </div>
        )}
      </div>

      <Modal open={configOpen} onClose={closeConfig} size="fullscreen">
        <Modal.Header style={filasVtmeModalHeaderStyle}>Configurar filas</Modal.Header>
        <Modal.Content scrolling style={{ ...filasVtmeModalContentStyle, maxHeight: 'calc(100vh - 140px)' }}>
          {configError && <Message negative content={configError} />}
          {configLoading ? (
            <Loader active inline="centered" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="button" icon labelPosition="left" onClick={addQueue}>
                  <Icon name="plus" />
                  Adicionar fila
                </Button>
              </div>
              {queuesDraft.length === 0 && (
                <Message info content="Nenhuma fila. Clique em Adicionar fila para começar." />
              )}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))',
                  gap: 16,
                  alignItems: 'stretch',
                }}
              >
                {queuesDraft.map((queue, index) => {
                  const blockedStatuses = new Set(
                    queuesDraft
                      .filter(other => other.localId !== queue.localId)
                      .flatMap(other => other.statuses.map(row => row.statusVtme).filter(Boolean))
                  );
                  return (
                    <StageConfigSection
                      key={queue.localId}
                      name={queue.name}
                      slaMinutes={queue.slaMinutes}
                      slaEntries={queue.slaEntries}
                      slaMoves={queue.slaMoves}
                      availableStatuses={availableStatuses}
                      blockedStatuses={blockedStatuses}
                      draft={queue.statuses}
                      canMoveUp={index > 0}
                      canMoveDown={index < queuesDraft.length - 1}
                      canRemove={queuesDraft.length > 1}
                      onNameChange={name => updateQueueName(queue.localId, name)}
                      onSlaChange={patch => updateQueueSla(queue.localId, patch)}
                      onMoveUp={() => moveQueue(queue.localId, -1)}
                      onMoveDown={() => moveQueue(queue.localId, 1)}
                      onRemoveQueue={() => removeQueue(queue.localId)}
                      onAdd={() => addStatusRow(queue.localId)}
                      onRemove={rowId => removeStatusRow(queue.localId, rowId)}
                      onChange={(rowId, patch) => updateStatusRow(queue.localId, rowId, patch)}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions style={filasVtmeModalActionsStyle}>
          <Button type="button" onClick={closeConfig} disabled={configSaving}>
            Cancelar
          </Button>
          <Button
            type="button"
            primary
            onClick={() => void saveConfig()}
            loading={configSaving}
            disabled={configLoading || configSaving}
          >
            Salvar
          </Button>
        </Modal.Actions>
      </Modal>
    </GlassPageWrapper>
  );
};

const queueSlaFieldTitleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: FILAS_VTME_THEME.muted,
  minHeight: 32,
  lineHeight: 1.25,
  display: 'flex',
  alignItems: 'flex-end',
};

const queueSlaFieldHintStyle: React.CSSProperties = {
  fontSize: 11,
  color: FILAS_VTME_THEME.muted,
  minHeight: 32,
  lineHeight: 1.35,
  marginTop: 4,
  marginBottom: 8,
};

const queueSlaFieldControlStyle: React.CSSProperties = {
  minHeight: 38,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
};

const queueSlaNumberInputProps = {
  fluid: true,
  style: { width: '100%', minWidth: 0, maxWidth: '100%' } as React.CSSProperties,
  input: {
    style: {
      textAlign: 'center' as const,
      minWidth: 0,
      width: '100%',
      minHeight: 38,
      paddingLeft: 10,
      paddingRight: 10,
      fontSize: 14,
      boxSizing: 'border-box' as const,
    },
  },
};

const QueueSlaField: React.FC<{
  title: string;
  hint: string;
  children: React.ReactNode;
}> = ({ title, hint, children }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      maxWidth: '100%',
      width: '100%',
    }}
  >
    <div style={queueSlaFieldTitleStyle}>{title}</div>
    <div style={queueSlaFieldHintStyle}>{hint}</div>
    <div style={queueSlaFieldControlStyle}>{children}</div>
  </div>
);

const statusSlaInputProps = {
  style: { flex: '1 1 0', minWidth: 0, width: '100%', maxWidth: '100%' } as React.CSSProperties,
  input: {
    style: {
      textAlign: 'center' as const,
      minWidth: 0,
      width: '100%',
      paddingLeft: 8,
      paddingRight: 8,
      boxSizing: 'border-box' as const,
    },
  },
};

const queueSlaDurationInputProps = {
  style: { flex: '1 1 0', minWidth: 0, width: '100%', maxWidth: '100%' } as React.CSSProperties,
  input: {
    style: {
      textAlign: 'center' as const,
      minWidth: 0,
      width: '100%',
      paddingLeft: 10,
      paddingRight: 10,
      boxSizing: 'border-box' as const,
    },
  },
};

const durationUnitSelectStyle = (compact: boolean): React.CSSProperties => ({
  flex: '0 0 auto',
  width: compact ? 68 : 104,
  minWidth: compact ? 68 : 104,
  maxWidth: compact ? 68 : 104,
  height: 38,
  border: '1px solid rgba(34, 36, 38, 0.15)',
  borderRadius: 4,
  background: '#fff',
  color: FILAS_VTME_THEME.text,
  fontSize: 13,
  fontWeight: 600,
  padding: compact ? '0 4px' : '0 8px',
  cursor: 'pointer',
  // select nativo abre acima do overflow da modal/tabela
  position: 'relative',
  zIndex: 2,
});

const DurationSlaInputs: React.FC<{
  totalMinutes: number;
  onChange: (totalMinutes: number) => void;
  compact?: boolean;
}> = ({ totalMinutes, onChange, compact = false }) => {
  const enabled = isSlaEnabled(totalMinutes);
  const safeMinutes = enabled ? Math.floor(totalMinutes) : 0;
  const [unit, setUnit] = useState<DurationUnit>(() => pickDurationUnit(Math.max(1, safeMinutes || 1)));
  const displayValue = enabled ? durationValueFromMinutes(safeMinutes, unit) : '';

  const unitLabel = (option: DurationUnit) => {
    if (!compact) {
      return DURATION_UNIT_OPTIONS.find(item => item.value === option)?.text || option;
    }
    if (option === 'days') return 'D';
    if (option === 'hours') return 'H';
    return 'Min';
  };

  return (
    <div style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <div
        style={{
          display: 'flex',
          gap: 6,
          alignItems: 'stretch',
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <div style={{ flex: '1 1 0', minWidth: 0, maxWidth: '100%' }}>
          <Input
            type="number"
            min={0}
            fluid
            placeholder="0"
            value={displayValue}
            {...(compact ? statusSlaInputProps : queueSlaDurationInputProps)}
            onChange={(_, data) => {
              const raw = String(data.value ?? '').trim();
              if (raw === '') {
                onChange(0);
                return;
              }
              const parsed = Number.parseInt(raw, 10);
              if (!Number.isFinite(parsed) || parsed <= 0) {
                onChange(0);
                return;
              }
              onChange(durationValueToMinutes(parsed, unit));
            }}
          />
        </div>
        <select
          aria-label="Unidade do SLA Tempo"
          value={unit}
          style={durationUnitSelectStyle(compact)}
          onChange={event => {
            const nextUnit = event.target.value as DurationUnit;
            if (!nextUnit || nextUnit === unit) return;
            setUnit(nextUnit);
            if (enabled) {
              const currentDisplay = durationValueFromMinutes(safeMinutes, unit);
              onChange(durationValueToMinutes(currentDisplay, nextUnit));
            }
          }}
        >
          {DURATION_UNIT_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {unitLabel(option.value)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

const StageConfigSection: React.FC<{
  name: string;
  slaMinutes: number;
  slaEntries: number;
  slaMoves: number;
  availableStatuses: string[];
  blockedStatuses: Set<string>;
  draft: DraftStatusRow[];
  canMoveUp: boolean;
  canMoveDown: boolean;
  canRemove: boolean;
  onNameChange: (name: string) => void;
  onSlaChange: (patch: Partial<Pick<DraftQueue, 'slaMinutes' | 'slaEntries' | 'slaMoves'>>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemoveQueue: () => void;
  onAdd: () => void;
  onRemove: (rowId: string) => void;
  onChange: (rowId: string, patch: Partial<Pick<DraftStatusRow, 'statusVtme' | 'slaMinutes' | 'slaEntries'>>) => void;
}> = ({
  name,
  slaMinutes,
  slaEntries,
  slaMoves,
  availableStatuses,
  blockedStatuses,
  draft,
  canMoveUp,
  canMoveDown,
  canRemove,
  onNameChange,
  onSlaChange,
  onMoveUp,
  onMoveDown,
  onRemoveQueue,
  onAdd,
  onRemove,
  onChange,
}) => {
  const usedInThisStage = useMemo(
    () => new Set(draft.map(row => row.statusVtme).filter(Boolean)),
    [draft]
  );

  return (
    <Segment style={filasVtmeQueueCardStyle}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <Input
          fluid
          value={name}
          onChange={(_, data) => onNameChange(String(data.value || ''))}
          placeholder="Nome da fila"
          style={{ flex: '1 1 220px', minWidth: 180 }}
        />
        <Button.Group size="mini" style={{ flex: '0 0 auto' }}>
          <Button type="button" icon disabled={!canMoveUp} onClick={onMoveUp} title="Subir">
            <Icon name="arrow up" />
          </Button>
          <Button type="button" icon disabled={!canMoveDown} onClick={onMoveDown} title="Descer">
            <Icon name="arrow down" />
          </Button>
          <Button type="button" icon onClick={onAdd} title="Adicionar status">
            <Icon name="plus" />
          </Button>
          <Button
            type="button"
            icon
            negative
            basic
            disabled={!canRemove}
            onClick={onRemoveQueue}
            title="Excluir fila"
          >
            <Icon name="trash" />
          </Button>
        </Button.Group>
      </div>
      <div
        style={{
          display: 'grid',
          // Quebra em 2+ linhas quando a largura do card não comporta os 3 campos.
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
          marginBottom: 16,
          alignItems: 'start',
          width: '100%',
          minWidth: 0,
        }}
      >
        <QueueSlaField
          title="SLA Tempo"
          hint="0 = desligado · comparado à mediana do tempo na fila"
        >
          <DurationSlaInputs
            totalMinutes={slaMinutes}
            onChange={minutes => onSlaChange({ slaMinutes: minutes })}
          />
        </QueueSlaField>
        <QueueSlaField
          title="SLA Entradas Fila"
          hint="0 = desligado · comparado à mediana de entradas"
        >
          <Input
            type="number"
            min={0}
            placeholder="0"
            value={slaEntries === 0 ? '' : slaEntries}
            {...queueSlaNumberInputProps}
            onChange={(_, data) => {
              const raw = String(data.value ?? '').trim();
              if (raw === '') {
                onSlaChange({ slaEntries: 0 });
                return;
              }
              const parsed = Number.parseInt(raw, 10);
              onSlaChange({
                slaEntries: Number.isFinite(parsed) && parsed > 0 ? parsed : 0,
              });
            }}
          />
        </QueueSlaField>
        <QueueSlaField
          title="SLA Movimentações"
          hint="0 = desligado · troca de status · mediana de movimentações"
        >
          <Input
            type="number"
            min={0}
            placeholder="0"
            value={slaMoves === 0 ? '' : slaMoves}
            {...queueSlaNumberInputProps}
            onChange={(_, data) => {
              const raw = String(data.value ?? '').trim();
              if (raw === '') {
                onSlaChange({ slaMoves: 0 });
                return;
              }
              const parsed = Number.parseInt(raw, 10);
              onSlaChange({
                slaMoves: Number.isFinite(parsed) && parsed > 0 ? parsed : 0,
              });
            }}
          />
        </QueueSlaField>
      </div>
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <Table celled unstackable style={{ minWidth: 720, tableLayout: 'fixed' }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ width: '36%' }}>Status</Table.HeaderCell>
              <Table.HeaderCell style={{ width: '36%' }}>
                SLA Tempo
                <div style={{ fontWeight: 400, fontSize: 11, color: FILAS_VTME_THEME.muted }}>
                  0 = desligado · por passagem · mediana no painel
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '20%' }}>
                SLA Entradas Status
                <div style={{ fontWeight: 400, fontSize: 11, color: FILAS_VTME_THEME.muted }}>
                  0 = desligado · vezes no status · mediana no painel
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '8%' }} />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {draft.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={4} style={{ color: FILAS_VTME_THEME.muted }}>
                  Nenhum status. Clique em + para adicionar.
                </Table.Cell>
              </Table.Row>
            )}
            {draft.map(row => {
              const options = availableStatuses
                .filter(status =>
                  status === row.statusVtme
                  || (!blockedStatuses.has(status) && !usedInThisStage.has(status))
                )
                .map(status => ({
                  key: `${row.id}-${status}`,
                  value: status,
                  text: status,
                }));

              return (
                <Table.Row key={row.id}>
                  <Table.Cell>
                    <Dropdown
                      placeholder="Selecione o status"
                      fluid
                      search
                      selection
                      options={options}
                      value={row.statusVtme || undefined}
                      onChange={(_, data) =>
                        onChange(row.id, { statusVtme: String(data.value || '') })
                      }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <DurationSlaInputs
                      compact
                      totalMinutes={row.slaMinutes}
                      onChange={minutes => onChange(row.id, { slaMinutes: minutes })}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Input
                      type="number"
                      min={0}
                      fluid
                      placeholder="0"
                      value={row.slaEntries === 0 ? '' : row.slaEntries}
                      {...statusSlaInputProps}
                      onChange={(_, data) => {
                        const raw = String(data.value ?? '').trim();
                        if (raw === '') {
                          onChange(row.id, { slaEntries: 0 });
                          return;
                        }
                        const parsed = Number.parseInt(raw, 10);
                        onChange(row.id, {
                          slaEntries: Number.isFinite(parsed) && parsed > 0 ? parsed : 0,
                        });
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Button
                      type="button"
                      icon
                      size="mini"
                      basic
                      negative
                      onClick={() => onRemove(row.id)}
                      title="Remover"
                    >
                      <Icon name="trash" />
                    </Button>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    </Segment>
  );
};

export default FilasVtmePage;
